import EventEmitter, { EVENT_TYPES } from '@lib/eventEmitter';

interface DisclaimerModalProps {
  disclaimer?:
    | 'vehicleTax'
    | 'electricVehicleTax'
    | 'mpgAndCo2'
    | 'electricRange'; // disclaimer is only technically optional for DisclainerWrapper parent 👀
}

interface DisclaimerLookup {
  component: () => JSX.Element;
  title: string;
}

const DisclaimerModal = ({
  disclaimer = 'vehicleTax',
}: DisclaimerModalProps) => {
  const closeModal = () => EventEmitter.emit(EVENT_TYPES.closeModal);

  const renderVehicleTaxDisclaimer = () => {
    return (
      <>
        <p>Vehicle tax depends on a vehicle&apos;s:</p>
        <ul className="ch-list ch-list--dot ch-measure ch-ml--1 ch-mb--2">
          <li>CO2 emissions</li>
          <li>Fuel type</li>
          <li>Engine size</li>
          <li>First registration date</li>
        </ul>
        <a
          className="ch-btn ch-btn--external-link ch-width--12"
          href="https://www.gov.uk/vehicle-tax-rate-tables"
          rel="noreferrer"
          target="_blank"
        >
          View latest vehicle tax rates
        </a>
      </>
    );
  };

  const renderElectricVehicleTaxDisclaimer = () => {
    return (
      <>
        {renderVehicleTaxDisclaimer()}
        <h2 className="ch-mt--6 ch-fs--4">Electric vehicles</h2>
        <p>
          Vehicle tax for electric, zero and low emission vehicles changed on 1
          April 2025.
        </p>
        <p>This change applied to both new and existing vehicles.</p>
        <a
          className="ch-btn ch-btn--external-link ch-width--12"
          href="https://www.gov.uk/guidance/vehicle-tax-for-electric-and-low-emissions-vehicles"
          rel="noreferrer"
          target="_blank"
        >
          More information
        </a>
      </>
    );
  };

  const renderMpgAndCo2Disclaimer = () => {
    return (
      <>
        <p>
          Depending on the age of the vehicle, MPG and CO2 may be quoted using
          either NEDC or WLTP testing standards.
        </p>
        <p>Figures are provided for comparison purposes.</p>
        <p className="ch-mb--0">
          Fuel consumption under real world driving conditions and the CO2
          produced will depend upon a number of factors, including any
          accessories fitted after registration, variations in driving styles,
          weather conditions and vehicle load.
        </p>
      </>
    );
  };

  const renderelectricRangeDisclaimer = () => {
    return (
      <>
        <p className="ch-mb--0">
          The pure electric range is provided for comparative purposes only and
          are based on laboratory testing. The actual figures under real world
          driving conditions will depend upon a number of factors, including any
          accessories fitted after registration, variations in driving styles,
          weather conditions, vehicle load and the health of the battery.
        </p>
      </>
    );
  };

  const disclaimersLookup: Record<string, DisclaimerLookup> = {
    vehicleTax: {
      component: renderVehicleTaxDisclaimer,
      title: 'Vehicle Tax',
    },
    electricVehicleTax: {
      component: renderElectricVehicleTaxDisclaimer,
      title: 'Vehicle Tax', // The sub-title for electric cars is inside the component. This could be refactored to be more readable.
    },
    mpgAndCo2: {
      component: renderMpgAndCo2Disclaimer,
      title: 'MPG & CO2',
    },
    electricRange: {
      component: renderelectricRangeDisclaimer,
      title: 'Electric range',
    },
  };

  return (
    <div className="ch-modal ch-modal--drawer ac-modal--disclaimer ch-bg--grey-1 ch-pt--1 sl:ch-pa--3">
      <button
        className="ch-modal__close ch-bg--grey-1 ch-display--none sl:ch-display--inline-flex"
        onClick={closeModal}
        type="button"
      >
        <span className="ch-reader">Close</span>
      </button>
      <div className="ch-modal__header ch-bg--grey-1 ch-bb--0">
        <h1 className="ch-fs--5 ch-mb--0">
          {disclaimersLookup[disclaimer].title}
        </h1>
      </div>
      <div className="ch-modal__body ch-ph--2 ch-pb--4">
        <div>{disclaimersLookup[disclaimer].component()}</div>
      </div>
      <div className="ch-modal__footer shadow--top sl:ch-display--none ch-bt--0 ch-bg--white ch-text--center">
        <button
          className="ch-btn ch-btn--link ac-modal__close--footer ch-pa--4"
          onClick={closeModal}
          type="button"
        >
          <span className="ch-reader">Close</span>
        </button>
      </div>
    </div>
  );
};

export default DisclaimerModal;
